// TripApp.jsx — עמוד עצמאי בנתיב /app
// שים ב: src/components/TripApp.jsx

import { useState, useEffect, useMemo } from 'react';
import TripPortalModal, { IOSPortalButton } from './TripPortal';
import { initializeApp, getApps } from 'firebase/app';
import {
    getFirestore, collection, query, where, getDocs,
    doc, updateDoc, arrayUnion
} from 'firebase/firestore';

const firebaseConfig = {
    apiKey: 'AIzaSyAnUB-I7W_zrFz9aPNHMVnKFBW0mOqymwU',
    authDomain: 'megedigital-2051f.firebaseapp.com',
    projectId: 'megedigital-2051f',
    storageBucket: 'megedigital-2051f.firebasestorage.app',
    messagingSenderId: '869349495873',
    appId: '1:869349495873:web:8e143ddb22acf42ef162c0',
};
const fbApp = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
const db = getFirestore(fbApp);

const C = {
    navy: '#0D1F4A', blue: '#1B3F8B', mid: '#2952A3',
    beige: '#E8E0D5', gold: '#D4A017', grey: '#94A3B8',
    greyDk: '#475569', white: '#FFFFFF', green: '#22C55E', red: '#EF4444',
};

const fmtDate = (iso) => {
    if (!iso) return '';
    try { return iso.split('-').reverse().join('/'); } catch { return iso; }
};

const SESSION_KEY = 'meged_web_session';
const saveSession = (docId) => {
    const expiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    localStorage.setItem(SESSION_KEY, JSON.stringify({ docId, expiry }));
};
const loadSession = () => {
    try {
        const s = JSON.parse(localStorage.getItem(SESSION_KEY) || '{}');
        if (s.docId && s.expiry && new Date() < new Date(s.expiry)) return s.docId;
    } catch { }
    return null;
};
const clearSession = () => localStorage.removeItem(SESSION_KEY);

export default function TripApp() {
    const [screen, setScreen] = useState('login');
    const [tripData, setTripData] = useState(null);
    const [docId, setDocId] = useState(null);
    const [tab, setTab] = useState('home');
    const [loading, setLoading] = useState(false);

    // בדוק session קיים
    useEffect(() => {
        const saved = loadSession();
        if (saved) { setDocId(saved); setScreen('pin'); }
    }, []);

    // עדכון title לפי מצב
    useEffect(() => {
        if (tripData) {
            document.title = `${tripData.travelerName} — מגד שמיים`;
        } else {
            document.title = 'תיק הטיול — מגד שמיים';
        }
    }, [tripData]);

    const handleLoginSuccess = (data, id) => {
        setTripData(data); setDocId(id);
        saveSession(id); setScreen('trip');
    };

    const handlePinSuccess = async (id) => {
        setLoading(true);
        try {
            const snap = await getDocs(query(collection(db, 'trips'), where('__name__', '==', id)));
            if (!snap.empty) { setTripData(snap.docs[0].data()); setScreen('trip'); }
        } catch { }
        setLoading(false);
    };

    const handleLogout = () => {
        clearSession(); setTripData(null); setDocId(null);
        setScreen('login'); setTab('home');
    };

    const refresh = async () => {
        if (!docId) return;
        setLoading(true);
        try {
            const snap = await getDocs(query(collection(db, 'trips'), where('__name__', '==', docId)));
            if (!snap.empty) setTripData(snap.docs[0].data());
        } catch { }
        setLoading(false);
    };

    return (
        <div style={{
            minHeight: '100dvh',
            background: C.navy,
            display: 'flex',
            flexDirection: 'column',
            direction: 'rtl',
            fontFamily: 'Assistant, Rubik, sans-serif',
            maxWidth: 480,
            margin: '0 auto',
        }}>

            {/* AppBar */}
            <div style={{
                background: C.blue,
                padding: 'max(16px, env(safe-area-inset-top)) 18px 14px',
                display: 'flex', alignItems: 'center', gap: 10,
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                flexShrink: 0,
                position: 'sticky', top: 0, zIndex: 100,
            }}>
                <span style={{ fontSize: 22 }}>✈️</span>
                <div style={{ flex: 1 }}>
                    <div style={{ color: C.white, fontWeight: 700, fontSize: 15 }}>מגד שמיים</div>
                    <div style={{ color: C.grey, fontSize: 11 }}>
                        {screen === 'trip' ? tripData?.travelerName : 'תיק הטיול הדיגיטלי'}
                    </div>
                </div>
                {screen === 'trip' && (
                    <button onClick={refresh}
                        style={{ background: 'none', border: 'none', color: C.grey, fontSize: 20, cursor: 'pointer', padding: 4 }}>
                        ⟳
                    </button>
                )}
                {screen === 'trip' && (
                    <button onClick={handleLogout}
                        style={{ background: 'none', border: 'none', color: C.grey, fontSize: 13, cursor: 'pointer', padding: '4px 8px' }}>
                        יציאה
                    </button>
                )}
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading && (
                    <div style={{ padding: 40, textAlign: 'center', color: C.grey, fontSize: 16 }}>⏳ טוען...</div>
                )}
                {!loading && screen === 'login' && <LoginScreen onSuccess={handleLoginSuccess} />}
                {!loading && screen === 'pin' && (
                    <PinScreen docId={docId} onSuccess={handlePinSuccess}
                        onForgot={() => { clearSession(); setScreen('login'); }} />
                )}
                {!loading && screen === 'trip' && tripData && (
                    <TripViewer tripData={tripData} docId={docId} tab={tab} setTab={setTab} />
                )}
            </div>

            {/* Bottom Nav */}
            {screen === 'trip' && <BottomNav tab={tab} setTab={setTab} />}
        </div>
    );
}

// ── Login ─────────────────────────────────────────────────
function LoginScreen({ onSuccess }) {
    const [id, setId] = useState('');
    const [pin, setPin] = useState('');
    const [phone, setPhone] = useState('');
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPin, setShowPin] = useState(false);

    const handleLogin = async () => {
        if (id.length !== 9) { setErr('מספר זהות: 9 ספרות'); return; }
        if (pin.length !== 4) { setErr('PIN: 4 ספרות'); return; }
        if (phone.length !== 10) { setErr('טלפון: 10 ספרות'); return; }
        setLoading(true); setErr('');
        try {
            const snap = await getDocs(query(collection(db, 'trips'), where('tripId', '==', id)));
            if (snap.empty) { setErr('לא נמצא תיק טיול'); setLoading(false); return; }
            const docSnap = snap.docs[0];
            const data = docSnap.data();

            // בדיקת נעילה
            if (data.lockedUntil && new Date() < new Date(data.lockedUntil)) {
                const remaining = Math.ceil((new Date(data.lockedUntil) - new Date()) / 60000);
                setErr(`החשבון נעול. נסה שוב בעוד ${remaining} דקות`);
                setLoading(false); return;
            }

            const attempts = data.loginAttempts || 0;

            // בדיקת PIN
            if (data.authCode?.toString() !== pin) {
                const newAttempts = attempts + 1;
                if (newAttempts >= 5) {
                    const lockUntil = new Date(Date.now() + 30 * 60 * 1000).toISOString();
                    await updateDoc(docSnap.ref, { loginAttempts: newAttempts, lockedUntil: lockUntil });
                    setErr('יותר מדי ניסיונות. החשבון ננעל ל-30 דקות');
                } else {
                    await updateDoc(docSnap.ref, { loginAttempts: newAttempts });
                    setErr(`פרטים שגויים. נותרו ${5 - newAttempts} ניסיונות`);
                }
                setLoading(false); return;
            }

            // בדיקת טלפון
            const saved = (data.travelerPhone || '').replace(/[\s\-\+]/g, '').replace(/^972/, '0');
            const input = phone.replace(/^972/, '0');
            if (saved !== input) {
                const newAttempts = attempts + 1;
                if (newAttempts >= 5) {
                    const lockUntil = new Date(Date.now() + 30 * 60 * 1000).toISOString();
                    await updateDoc(docSnap.ref, { loginAttempts: newAttempts, lockedUntil: lockUntil });
                    setErr('יותר מדי ניסיונות. החשבון ננעל ל-30 דקות');
                } else {
                    await updateDoc(docSnap.ref, { loginAttempts: newAttempts });
                    setErr(`פרטים שגויים. נותרו ${5 - newAttempts} ניסיונות`);
                }
                setLoading(false); return;
            }

            await updateDoc(docSnap.ref, { loginAttempts: 0, lockedUntil: null, lastLogin: new Date().toISOString() });
            onSuccess(data, docSnap.id);
        } catch (e) { setErr('שגיאה: ' + e.message); }
        setLoading(false);
    };
    return (
        <div style={{ padding: 28 }}>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <div style={{ fontSize: 48, marginBottom: 10 }}>🔐</div>
                <div style={{ color: C.white, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>כניסה לתיק הטיול</div>
                <div style={{ color: C.grey, fontSize: 14 }}>הזן את הפרטים שקיבלת מהסוכן</div>
            </div>
            <Fld label="מספר זהות" placeholder="9 ספרות" value={id}
                onChange={v => setId(v.replace(/\D/g, '').slice(0, 9))} mode="numeric" />
            <div style={{ position: 'relative' }}>
                <Fld label='קוד PIN' placeholder="4 ספרות" value={pin}
                    onChange={v => setPin(v.replace(/\D/g, '').slice(0, 4))} mode="numeric" secret={!showPin} />
                <button onClick={() => setShowPin(!showPin)}
                    style={{ position: 'absolute', left: 14, top: 36, background: 'none', border: 'none', color: C.grey, cursor: 'pointer', fontSize: 16 }}>
                    {showPin ? '🙈' : '👁'}
                </button>
            </div>
            <Fld label="טלפון" placeholder="05XXXXXXXX" value={phone}
                onChange={v => setPhone(v.replace(/\D/g, '').slice(0, 10))} mode="numeric" />
            {err && <ErrBox msg={err} />}
            <Btn label={loading ? 'מתחבר...' : 'כניסה לתיק הטיול'} onClick={handleLogin} disabled={loading} gold />
        </div>
    );
}

// ── PIN ───────────────────────────────────────────────────
function PinScreen({ docId, onSuccess, onForgot }) {
    const [pin, setPin] = useState('');
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPin, setShowPin] = useState(false);

    const handlePin = async () => {
        if (pin.length !== 4) { setErr('PIN: 4 ספרות'); return; }
        setLoading(true); setErr('');
        try {
            const snap = await getDocs(query(collection(db, 'trips'), where('__name__', '==', docId)));
            if (snap.empty) { onForgot(); return; }
            if (snap.docs[0].data().authCode?.toString() !== pin) {
                setErr('קוד שגוי'); setLoading(false); return;
            }
            onSuccess(docId);
        } catch { setErr('שגיאה'); }
        setLoading(false);
    };

    return (
        <div style={{ padding: 28 }}>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <div style={{ fontSize: 48, marginBottom: 10 }}>👤</div>
                <div style={{ color: C.white, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>ברוך השב</div>
                <div style={{ color: C.grey, fontSize: 14 }}>הזן קוד PIN לכניסה מהירה</div>
            </div>
            <div style={{ position: 'relative' }}>
                <Fld label="קוד PIN" placeholder="4 ספרות" value={pin}
                    onChange={v => setPin(v.replace(/\D/g, '').slice(0, 4))} mode="numeric" secret={!showPin} />
                <button onClick={() => setShowPin(!showPin)}
                    style={{ position: 'absolute', left: 14, top: 36, background: 'none', border: 'none', color: C.grey, cursor: 'pointer', fontSize: 16 }}>
                    {showPin ? '🙈' : '👁'}
                </button>
            </div>
            {err && <ErrBox msg={err} />}
            <Btn label={loading ? 'מתחבר...' : 'כניסה'} onClick={handlePin} disabled={loading} gold />
            <button onClick={onForgot}
                style={{ width: '100%', background: 'none', border: 'none', color: C.grey, fontSize: 13, cursor: 'pointer', padding: 12, marginTop: 4 }}>
                כניסה עם פרטים מלאים
            </button>
        </div>
    );
}

// ── TripViewer + כל הטאבים — ייבוא מ-TripPortal ──────────
// (הטאבים זהים לחלוטין — לא כופלים קוד)
function TripViewer({ tripData, docId, tab, setTab }) {
    // ייבוא דינמי של הטאבים מ-TripPortal
    const tabs = {
        home: () => <HomeTab tripData={tripData} setTab={setTab} />,
        itinerary: () => <ItineraryTab tripData={tripData} />,
        docs: () => <DocsTab tripData={tripData} />,
        contacts: () => <ContactsTab tripData={tripData} docId={docId} />,
        expenses: () => <ExpensesTab tripData={tripData} docId={docId} />,
    };
    return tabs[tab] ? tabs[tab]() : null;
}

// ── Bottom Nav ────────────────────────────────────────────
function BottomNav({ tab, setTab }) {
    return (
        <div style={{
            background: C.navy,
            borderTop: '1px solid rgba(27,63,139,0.4)',
            display: 'flex',
            padding: 'env(safe-area-inset-bottom) 0 8px',
            flexShrink: 0,
            position: 'sticky', bottom: 0,
        }}>
            {[
                { id: 'home', icon: '🏠', label: 'בית' },
                { id: 'itinerary', icon: '🗺️', label: 'מסלול' },
                { id: 'docs', icon: '📁', label: 'מסמכים' },
                { id: 'contacts', icon: '👥', label: 'קשר' },
                { id: 'expenses', icon: '💸', label: 'הוצאות' },
            ].map(({ id, icon, label }) => (
                <button key={id} onClick={() => setTab(id)}
                    style={{
                        flex: 1, background: 'none', border: 'none', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '8px 0',
                    }}>
                    <span style={{ fontSize: 22 }}>{icon}</span>
                    <span style={{ fontSize: 10, color: tab === id ? C.white : C.greyDk, fontWeight: tab === id ? 700 : 400 }}>
                        {label}
                    </span>
                    {tab === id && <div style={{ width: 4, height: 4, borderRadius: '50%', background: C.gold }} />}
                </button>
            ))}
        </div>
    );
}

// ── Tabs (עותק מ-TripPortal לעמוד עצמאי) ─────────────────
function HomeTab({ tripData, setTab }) {
  const [countdown, setCountdown] = useState({ days:0, hours:0, mins:0, secs:0 });
  const now    = new Date();
 const startDt = useMemo(() => tripData.startDate ? new Date(tripData.startDate) : null, [tripData.startDate]);
const endDt   = useMemo(() => tripData.endDate   ? new Date(tripData.endDate)   : null, [tripData.endDate]);
  const flights  = tripData.flights   || [];
  const docs     = tripData.documents || [];
  const contacts = tripData.contacts  || [];
  const expenses = tripData.expenses  || [];

  // סטטוס טיול
  let statusText = '';
  let isBefore = false;
  if (startDt && endDt) {
    if (now < startDt) { statusText = `הטיול המושלם שלך יהיה בעוד ${Math.ceil((startDt-now)/86400000)} ימים ✈️`; isBefore = true; }
    else if (now > endDt) statusText = 'הטיול הסתיים — תודה שבחרת במגד שמיים 🎉';
    else statusText = `אתה בטיול! יום ${Math.ceil((now-startDt)/86400000)} מתוך ${Math.ceil((endDt-startDt)/86400000)} 🌍`;
  }

  // ספירה לאחור
  useEffect(() => {
    if (!isBefore || !startDt) return;
    const tick = () => {
      const diff = startDt - new Date();
      if (diff <= 0) return;
      setCountdown({
        days:  Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        mins:  Math.floor((diff % 3600000)  / 60000),
        secs:  Math.floor((diff % 60000)    / 1000),
      });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [isBefore, startDt]);

  // פורמט שעת טיסה
  const fmtTime = (dt) => {
    if (!dt) return '--:--';
    if (dt.includes('T')) return dt.split('T')[1]?.slice(0,5);
    if (dt.includes(' ')) return dt.split(' ')[1]?.slice(0,5);
    return dt.length >= 5 ? dt.slice(0,5) : dt;
  };

  const isValidFlight = (f) => {
    if (!f) return false;
    const dep = (f.departureAirport||'').trim();
    const arr = (f.arrivalAirport||'').trim();
    return dep && arr && dep !== arr;
  };

  return (
    <div style={{ padding:20 }}>
      {tripData.tripTitle && (
        <div style={{ color:C.gold, fontWeight:600, fontSize:13, marginBottom:4 }}>{tripData.tripTitle}</div>
      )}
      <div style={{ color:C.white, fontWeight:800, fontSize:22, marginBottom:4 }}>
        ברוך הבא, {tripData.travelerName}! 👋
      </div>
      {statusText && (
        <div style={{ color:'rgba(255,255,255,0.7)', fontSize:14, marginBottom:16 }}>{statusText}</div>
      )}

      {/* כרטיס ראשי */}
      <div style={{ background:`linear-gradient(135deg,${C.blue},${C.mid})`, borderRadius:20, padding:20, marginBottom:16, boxShadow:'0 8px 24px rgba(27,63,139,0.4)' }}>
        <div style={{ color:C.white, fontWeight:900, fontSize:26, marginBottom:2 }}>{tripData.destination}</div>
        <div style={{ color:'rgba(255,255,255,0.55)', fontSize:12, marginBottom:4 }}>
          {(tripData.itinerary||[]).length} ימים • {docs.length} מסמכים
        </div>
        {tripData.startDate && (
          <div style={{ color:'rgba(255,255,255,0.55)', fontSize:12, direction:'ltr', textAlign:'right', marginBottom:12 }}>
            {fmtDate(tripData.startDate)} — {fmtDate(tripData.endDate)}
          </div>
        )}

        {/* טיסות */}
        {flights.filter(isValidFlight).slice(0,2).map((f,i) => {
          const airline = (f.airline||'').split('/')[0].trim();
          return (
            <div key={i} style={{ background:'rgba(255,255,255,0.08)', borderRadius:12, padding:'10px 14px', marginBottom:8, border:'1px solid rgba(255,255,255,0.15)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                {/* צד שמאל — יציאה */}
                <div style={{ textAlign:'right' }}>
                  <div style={{ color:C.white, fontWeight:900, fontSize:20 }}>{f.departureAirport}</div>
                  {f.departureCity && <div style={{ color:C.grey, fontSize:10 }}>{f.departureCity}</div>}
                  <div style={{ color:C.beige, fontSize:12, fontWeight:700 }}>{fmtTime(f.departureTime)}</div>
                </div>
                {/* אמצע */}
                <div style={{ flex:1, textAlign:'center' }}>
                  <div style={{ height:1, background:'rgba(255,255,255,0.2)', position:'relative', margin:'4px 0' }}>
                    <span style={{ position:'absolute', top:-8, left:'50%', transform:'translateX(-50%)', fontSize:14 }}>✈️</span>
                  </div>
                  {(airline || f.flightNumber) && (
                    <div style={{ color:C.grey, fontSize:9, marginTop:8 }}>
                      {[airline, f.flightNumber].filter(Boolean).join(' • ')}
                    </div>
                  )}
                  <div style={{ color:'rgba(255,255,255,0.5)', fontSize:9, marginTop:2 }}>
                    {i===0 ? 'הלוך' : 'חזור'}
                  </div>
                </div>
                {/* צד ימין — הגעה */}
                <div style={{ textAlign:'left' }}>
                  <div style={{ color:C.white, fontWeight:900, fontSize:20 }}>{f.arrivalAirport}</div>
                  {f.arrivalCity && <div style={{ color:C.grey, fontSize:10 }}>{f.arrivalCity}</div>}
                  <div style={{ color:C.beige, fontSize:12, fontWeight:700 }}>{fmtTime(f.arrivalTime)}</div>
                </div>
              </div>
            </div>
          );
        })}

        {/* ספירה לאחור */}
        {isBefore ? (
          <div style={{ background:'rgba(255,255,255,0.15)', borderRadius:14, padding:'12px 16px', marginTop:8 }}>
            <div style={{ color:C.white, fontSize:13, fontWeight:600, marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
              ⏰ נותרו {countdown.days} ימים לטיול
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'center', direction:'ltr' }}>
              {[
                { val: String(countdown.days).padStart(2,'0'),  label:'Days'  },
                { val: String(countdown.hours).padStart(2,'0'), label:'Hours' },
                { val: String(countdown.mins).padStart(2,'0'),  label:'Min'   },
                { val: String(countdown.secs).padStart(2,'0'),  label:'Sec'   },
              ].map(({ val, label }, idx) => (
                <div key={label} style={{ display:'flex', alignItems:'center', gap: idx<3?6:0 }}>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ background:'rgba(255,255,255,0.2)', borderRadius:8, padding:'6px 10px', color:C.white, fontWeight:900, fontSize:24, minWidth:44 }}>{val}</div>
                    <div style={{ color:C.white, fontSize:10, marginTop:2 }}>{label}</div>
                  </div>
                  {idx < 3 && <span style={{ color:C.white, fontWeight:900, fontSize:20, marginBottom:14 }}>:</span>}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ background:'rgba(255,255,255,0.15)', borderRadius:14, padding:'12px 16px', marginTop:8, display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:20 }}>{now > endDt ? '✅' : '✈️'}</span>
            <span style={{ color:C.white, fontWeight:600, fontSize:14 }}>{statusText}</span>
          </div>
        )}
      </div>

      {/* סטטיסטיקות */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10, marginBottom:16 }}>
        {[
          { emoji:'📅', val:(tripData.itinerary||[]).length, label:'ימים',    tab:null      },
          { emoji:'🗺️', val:(tripData.itinerary||[]).length, label:'אתרים',   tab:'itinerary'},
          { emoji:'📄', val:docs.length,                     label:'מסמכים',  tab:'docs'    },
          { emoji:'👥', val:contacts.length,                 label:'קשר',     tab:'contacts'},
        ].map(({ emoji, val, label, tab:t }) => (
          <button key={label} onClick={() => t && setTab(t)} style={{
            background:'rgba(27,63,139,0.18)', border:`1px solid ${t?'rgba(27,63,139,0.7)':'rgba(27,63,139,0.45)'}`,
            borderRadius:14, padding:'12px 6px', cursor: t?'pointer':'default',
            display:'flex', flexDirection:'column', alignItems:'center', gap:4,
          }}>
            <span style={{ fontSize:18 }}>{emoji}</span>
            <span style={{ color:C.white, fontWeight:800, fontSize:18 }}>{val}</span>
            <span style={{ color:C.grey, fontSize:10 }}>{label}</span>
          </button>
        ))}
      </div>

      {/* מלון משותף */}
      {tripData.sharedHotel && tripData.sharedAccommodation?.hotelName && (
        <div style={{ background:'rgba(27,63,139,0.18)', border:'1px solid rgba(27,63,139,0.45)', borderRadius:14, padding:14 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
            <span>🏨</span>
            <span style={{ color:C.white, fontWeight:700 }}>{tripData.sharedAccommodation.hotelName}</span>
          </div>
          {tripData.sharedAccommodation.checkoutTime && (
            <div style={{ color:C.grey, fontSize:12 }}>Checkout: {tripData.sharedAccommodation.checkoutTime}</div>
          )}
          {tripData.sharedAccommodation.freeCancellation != null && (
            <div style={{ fontSize:12, marginTop:4, color:tripData.sharedAccommodation.freeCancellation?'#34D399':C.red }}>
              {tripData.sharedAccommodation.freeCancellation ? '✓ ביטול חינם' : '✗ ללא ביטול חינם'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function ItineraryTab({ tripData }) {
  const [openDay, setOpenDay] = useState(null);
  const itinerary = tripData.itinerary || [];
  const sharedAcc = tripData.sharedHotel ? tripData.sharedAccommodation : null;
  const car       = tripData.car;
  const pickupIdx = car ? parseInt(car.pickupDayIdx ?? -1) : -1;
  const returnIdx = car ? parseInt(car.returnDayIdx ?? itinerary.length - 1) : -1;

  return (
    <div style={{ padding:16 }}>
      {itinerary.map((day, i) => {
        const acc  = day.accommodation?.hotelName ? day.accommodation : sharedAcc;
        const acts = (day.activities||[]).filter(a => a.title || a.description);
        const isOpen   = openDay === i;
        const isPickup = car && pickupIdx === i;
        const isReturn = car && returnIdx === i;
        const showCar  = isPickup || isReturn;

        return (
          <div key={i} style={{
            background:'rgba(27,63,139,0.15)',
            border:`1px solid ${showCar ? 'rgba(212,160,23,0.55)' : 'rgba(27,63,139,0.4)'}`,
            borderRadius:14, marginBottom:10, overflow:'hidden',
          }}>
            <button onClick={() => setOpenDay(isOpen ? null : i)}
              style={{ width:'100%', background:'none', border:'none', cursor:'pointer', padding:'12px 14px', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{
                width:32, height:32, borderRadius:9,
                background: showCar ? 'rgba(212,160,23,0.2)' : C.blue,
                flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
                color: showCar ? C.gold : C.white, fontWeight:800, fontSize:13,
              }}>{i+1}</div>
              <div style={{ flex:1, textAlign:'right' }}>
                <div style={{ color:C.white, fontWeight:600, fontSize:14 }}>{day.dayTitle || `יום ${i+1}`}</div>
                {showCar && (
                  <div style={{ display:'flex', gap:6, marginTop:3, flexWrap:'wrap' }}>
                    {isPickup && (
                      <span style={{ background:'rgba(212,160,23,0.12)', border:'1px solid rgba(212,160,23,0.4)', borderRadius:6, padding:'1px 7px', color:C.gold, fontSize:10, fontWeight:600 }}>
                        🚗 איסוף רכב
                      </span>
                    )}
                    {isReturn && (
                      <span style={{ background:'rgba(212,160,23,0.12)', border:'1px solid rgba(212,160,23,0.4)', borderRadius:6, padding:'1px 7px', color:C.gold, fontSize:10, fontWeight:600 }}>
                        🔄 החזרת רכב
                      </span>
                    )}
                  </div>
                )}
                <div style={{ color:C.grey, fontSize:11 }}>{fmtDate(day.date)}</div>
              </div>
              <span style={{ color:C.grey, fontSize:11 }}>{isOpen ? '▲' : '▼'}</span>
            </button>

            {isOpen && (
              <div style={{ padding:'0 14px 12px' }}>
                {/* פעילויות */}
                {acts.map((act, j) => (
                  <div key={j} style={{ display:'flex', gap:8, marginBottom:8, alignItems:'flex-start' }}>
                    {act.time && (
                      <span style={{ background:'rgba(27,63,139,0.3)', borderRadius:7, padding:'2px 7px', color:C.white, fontSize:11, flexShrink:0 }}>
                        {act.time}
                      </span>
                    )}
                    <div style={{ flex:1 }}>
                      {act.title && <div style={{ color:C.white, fontWeight:600, fontSize:13 }}>{act.title}</div>}
                      {act.description && <div style={{ color:C.grey, fontSize:12 }}>{act.description}</div>}
                      <div style={{ display:'flex', gap:12, marginTop:4, flexWrap:'wrap' }}>
                        {act.mapsLink && (
                          <a href={act.mapsLink} target="_blank" rel="noopener noreferrer"
                            style={{ color:'#34D399', fontSize:12, textDecoration:'none' }}>
                            📍 פתח במפות
                          </a>
                        )}
                        {act.ticketUrl && (
                          <a href={act.ticketUrl} target="_blank" rel="noopener noreferrer"
                            style={{ color:'#EAB308', fontSize:12, textDecoration:'none' }}>
                            🎫 פתח כרטיס
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* מלון */}
                {acc?.hotelName && (
                  <div style={{ background:'rgba(27,63,139,0.25)', borderRadius:10, padding:10, marginTop:6 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                      <span>🏨</span>
                      <div style={{ flex:1 }}>
                        <div style={{ color:C.white, fontWeight:600, fontSize:13 }}>{acc.hotelName}</div>
                        {acc.checkoutTime && <div style={{ color:C.grey, fontSize:11 }}>Checkout: {acc.checkoutTime}</div>}
                        {acc.freeCancellation != null && (
                          <div style={{ fontSize:11, marginTop:2, color:acc.freeCancellation?'#34D399':C.red }}>
                            {acc.freeCancellation ? '✓ ביטול חינם' : '✗ ללא ביטול חינם'}
                          </div>
                        )}
                        {acc.bookingRating && (
                          <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4 }}>
                            <span style={{ background:'#003580', borderRadius:6, padding:'2px 8px', color:C.white, fontSize:11, fontWeight:800 }}>
                              {acc.bookingRating} ★
                            </span>
                            <span style={{ color:C.grey, fontSize:10 }}>Booking.com</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      {acc.mapsLink && (
                        <a href={acc.mapsLink} target="_blank" rel="noopener noreferrer" style={{ background:'rgba(52,211,153,0.15)', border:'1px solid rgba(52,211,153,0.4)', borderRadius:8, padding:'4px 10px', color:'#34D399', fontSize:12, textDecoration:'none' }}>
                          🗺️ מפה
                        </a>
                      )}
                      {acc.bookingLink && (
                        <a href={acc.bookingLink} target="_blank" rel="noopener noreferrer" style={{ background:'rgba(0,97,255,0.15)', border:'1px solid rgba(0,97,255,0.4)', borderRadius:8, padding:'4px 10px', color:'#60A5FA', fontSize:12, textDecoration:'none' }}>
                          🏨 Booking
                        </a>
                      )}
                      {acc.confirmationUrl && (
                        <a href={acc.confirmationUrl} target="_blank" rel="noopener noreferrer" style={{ background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.4)', borderRadius:8, padding:'4px 10px', color:C.red, fontSize:12, textDecoration:'none' }}>
                          📄 אישור
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* רכב שכור */}
                {showCar && car && (
                  <div style={{ background:'rgba(212,160,23,0.05)', border:'1px solid rgba(212,160,23,0.3)', borderRadius:12, padding:12, marginTop:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                      <span>🚗</span>
                      <span style={{ color:C.gold, fontWeight:700, fontSize:14 }}>
                        {car.company ? `רכב שכור — ${car.company}` : 'רכב שכור'}
                      </span>
                    </div>

                    {isPickup && (
                      <div style={{ marginBottom:8 }}>
                        <div style={{ color:C.gold, fontWeight:600, fontSize:13, marginBottom:4 }}>📍 איסוף</div>
                        {car.pickupLocation && (
                          <div style={{ color:C.white, fontSize:13, marginBottom:6 }}>📍 {car.pickupLocation}</div>
                        )}
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                          {car.pickupMapsLink && (
                            <a href={car.pickupMapsLink} target="_blank" rel="noopener noreferrer" style={{ background:'rgba(52,211,153,0.15)', border:'1px solid rgba(52,211,153,0.4)', borderRadius:8, padding:'4px 10px', color:'#34D399', fontSize:12, textDecoration:'none' }}>
                              🗺️ מיקום איסוף
                            </a>
                          )}
                          {car.confirmationUrl && (
                            <a href={car.confirmationUrl} target="_blank" rel="noopener noreferrer" style={{ background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.4)', borderRadius:8, padding:'4px 10px', color:C.red, fontSize:12, textDecoration:'none' }}>
                              📄 אישור השכרה
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {isPickup && isReturn && (
                      <div style={{ height:1, background:'rgba(212,160,23,0.2)', margin:'8px 0' }} />
                    )}

                    {isReturn && (
                      <div>
                        <div style={{ color:C.grey, fontWeight:600, fontSize:13, marginBottom:4 }}>🔄 החזרה</div>
                        {car.returnLocation && (
                          <div style={{ color:C.white, fontSize:13, marginBottom:6 }}>📍 {car.returnLocation}</div>
                        )}
                        {car.returnMapsLink && (
                          <a href={car.returnMapsLink} target="_blank" rel="noopener noreferrer" style={{ background:'rgba(96,165,250,0.15)', border:'1px solid rgba(96,165,250,0.4)', borderRadius:8, padding:'4px 10px', color:'#60A5FA', fontSize:12, textDecoration:'none' }}>
                            🗺️ מיקום החזרה
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CarRow({ car, isPickup }) {
    const loc = isPickup ? car.pickupLocation : car.returnLocation;
    const maps = isPickup ? car.pickupMapsLink : car.returnMapsLink;
    return (
        <div style={{ background: `rgba(212,160,23,0.1)`, border: `1px solid rgba(212,160,23,0.3)`, borderRadius: 10, padding: 10, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{isPickup ? '🚗' : '🏁'}</span>
            <div style={{ flex: 1 }}>
                <div style={{ color: C.gold, fontWeight: 600, fontSize: 13 }}>{isPickup ? 'איסוף' : 'החזרה'} — {car.company}</div>
                {loc && <div style={{ color: C.grey, fontSize: 12 }}>{loc}</div>}
            </div>
            {maps && <a href={maps} target="_blank" rel="noopener noreferrer" style={{ color: '#34D399', fontSize: 18 }}>📍</a>}
        </div>
    );
}

function DocsTab({ tripData }) {
    const docs = tripData.documents || [];
    const CAT = { flight: '✈️', insurance: '🛡️', passport: '🛂', hotel: '🏨', other: '📄' };
    const LBL = { flight: 'כרטיס טיסה', insurance: 'פוליסת ביטוח', passport: 'צילום דרכון', hotel: 'אישור מלון', other: 'מסמך' };
    if (!docs.length) return <Empty icon="📁" text="אין מסמכים בתיק" />;
    return (
        <div style={{ padding: 16 }}>
            {docs.map((g, i) => (
                <div key={i} style={{ background: 'rgba(27,63,139,0.15)', border: '1px solid rgba(27,63,139,0.4)', borderRadius: 14, marginBottom: 10, padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: 24 }}>{CAT[g.category] || '📄'}</span>
                        <span style={{ color: C.white, fontWeight: 700 }}>{LBL[g.category] || 'מסמך'}</span>
                    </div>
                    {(g.files || []).map((f, j) => (
                        <div key={j} style={{ background: 'rgba(13,31,74,0.5)', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ color: C.grey }}>📎</span>
                            <span style={{ color: C.white, fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label || f.name}</span>
                            {f.url && <a href={f.url} target="_blank" rel="noopener noreferrer" style={{ background: C.blue, color: C.white, borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>פתח</a>}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function ContactsTab({ tripData, docId }) {
    const [contacts, setContacts] = useState(tripData.contacts || []);
    const tips = tripData.tips || [];
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [phone, setPhone] = useState('');
    const [saving, setSaving] = useState(false);
    const TI = { food: '🍽️', transport: '🚌', safety: '🛡️', general: '💡', money: '💵', culture: '🎭' };
    const TL = { food: 'אוכל', transport: 'תחבורה', safety: 'בטיחות', general: 'כללי', money: 'כסף', culture: 'תרבות' };

    const save = async () => {
        if (!name.trim()) return;
        setSaving(true);
        const entry = { name: name.trim(), role: role.trim(), phone: phone.trim() };
        try {
            await updateDoc(doc(db, 'trips', docId), { contacts: arrayUnion(entry) });
            setContacts(prev => [...prev, entry]);
            setAdding(false); setName(''); setRole(''); setPhone('');
        } catch { }
        setSaving(false);
    };

    return (
        <div style={{ padding: 16 }}>
            <div style={{ background: 'rgba(212,160,23,0.1)', border: '1px solid rgba(212,160,23,0.4)', borderRadius: 14, padding: 14, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(212,160,23,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>✈️</div>
                <div style={{ flex: 1 }}>
                    <div style={{ color: C.gold, fontWeight: 700 }}>מגד שמיים</div>
                    <div style={{ color: C.grey, fontSize: 12 }}>megedshamaim@gmail.com</div>
                </div>
                <a href="mailto:megedshamaim@gmail.com" style={{ background: 'rgba(212,160,23,0.2)', border: '1px solid rgba(212,160,23,0.4)', borderRadius: 10, padding: 8, fontSize: 18, textDecoration: 'none' }}>✉️</a>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: C.white, fontWeight: 700 }}>👥 אנשי קשר</span>
                <button onClick={() => setAdding(!adding)} style={{ background: 'rgba(27,63,139,0.3)', border: '1px solid rgba(27,63,139,0.5)', borderRadius: 8, padding: '4px 10px', color: C.grey, fontSize: 12, cursor: 'pointer' }}>+ הוסף</button>
            </div>

            {adding && (
                <div style={{ background: 'rgba(27,63,139,0.2)', borderRadius: 12, padding: 12, marginBottom: 10 }}>
                    <Fld label="שם" value={name} onChange={setName} />
                    <Fld label="תפקיד" value={role} onChange={setRole} />
                    <Fld label="טלפון" value={phone} onChange={setPhone} mode="numeric" />
                    <Btn label={saving ? 'שומר...' : 'שמור'} onClick={save} disabled={saving} />
                </div>
            )}

            {contacts.map((ct, i) => (
                <div key={i} style={{ background: 'rgba(27,63,139,0.15)', border: '1px solid rgba(27,63,139,0.4)', borderRadius: 12, padding: 12, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(27,63,139,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.beige, fontSize: 18 }}>👤</div>
                    <div style={{ flex: 1 }}>
                        <div style={{ color: C.white, fontWeight: 600 }}>{ct.name}</div>
                        {ct.role && <div style={{ color: C.grey, fontSize: 12 }}>{ct.role}</div>}
                        {ct.phone && <div style={{ color: C.grey, fontSize: 12 }}>{ct.phone}</div>}
                    </div>
                    {ct.phone && <a href={`https://wa.me/${ct.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, padding: 8, fontSize: 18, textDecoration: 'none' }}>💬</a>}
                </div>
            ))}

            {tips.length > 0 && (
                <>
                    <div style={{ color: C.white, fontWeight: 700, margin: '16px 0 8px' }}>💡 טיפים לטיול</div>
                    {tips.map((tip, i) => (
                        <div key={i} style={{ background: 'rgba(27,63,139,0.12)', border: '1px solid rgba(27,63,139,0.3)', borderRadius: 12, padding: 12, marginBottom: 8, display: 'flex', gap: 10 }}>
                            <span style={{ fontSize: 20 }}>{TI[tip.category] || '💡'}</span>
                            <div>
                                <div style={{ color: C.grey, fontSize: 11, fontWeight: 600 }}>{TL[tip.category] || 'כללי'}</div>
                                <div style={{ color: C.white, fontSize: 13 }}>{tip.text}</div>
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}

const CURRENCY_INFO = {
  ILS:{symbol:'₪',name:'שקל ישראלי'},USD:{symbol:'$',name:'דולר אמריקאי'},
  EUR:{symbol:'€',name:'אירו'},GBP:{symbol:'£',name:'פאונד בריטי'},
  CHF:{symbol:'Fr',name:'פרנק שוויצרי'},JPY:{symbol:'¥',name:'ין יפני'},
  TRY:{symbol:'₺',name:'לירה טורקית'},GEL:{symbol:'₾',name:'לארי גיאורגי'},
  THB:{symbol:'฿',name:'בהט תאילנדי'},AED:{symbol:'د.إ',name:'דירהם אמיראתי'},
  PLN:{symbol:'zł',name:'זלוטי פולני'},CZK:{symbol:'Kč',name:"קורונה צ'כית"},
  HUF:{symbol:'Ft',name:'פורינט הונגרי'},SEK:{symbol:'kr',name:'כתר שוודי'},
  NOK:{symbol:'kr',name:'כתר נורווגי'},DKK:{symbol:'kr',name:'כתר דני'},
};
const curSym  = (c) => CURRENCY_INFO[c]?.symbol || c;
const curName = (c) => CURRENCY_INFO[c]?.name   || c;

function ExpensesTab({ tripData, docId }) {
  const [expenses, setExpenses] = useState(tripData.expenses||[]);
  const [rates, setRates]       = useState({});
  const [showConverter, setShowConverter] = useState(false);
  const [converterVal, setConverterVal]   = useState('');
  const [fromLocal, setFromLocal]         = useState(true);
  const lc = tripData.localCurrency || '';
  const [adding, setAdding]   = useState(false);
  const [cat, setCat]         = useState('');
  const [amount, setAmount]   = useState('');
  const [currency, setCurrency] = useState('LOCAL');
  const [note, setNote]       = useState('');
  const [date, setDate]       = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving]   = useState(false);

  const CATS = [
    {id:'flight',label:'טיסה',icon:'✈️'},{id:'hotel',label:'מלון',icon:'🏨'},
    {id:'car',label:'רכב שכור',icon:'🚗'},{id:'fuel',label:'דלק',icon:'⛽'},
    {id:'taxi',label:'מונית / אובר',icon:'🚕'},{id:'shopping',label:'קניות',icon:'🛍️'},
    {id:'restaurant',label:'מסעדות',icon:'🍽️'},{id:'other',label:'אחר',icon:'📌'},
  ];
  const ci  = (id) => CATS.find(c=>c.id===id) || {icon:'📌', label:id};
  const sym = (c)  => c==='ILS'?'₪':c==='USD'?'$':lc?curSym(lc):'';

  // שליפת שערי חליפין
  useEffect(() => {
    fetch('https://api.exchangerate-api.com/v4/latest/ILS')
      .then(r=>r.json())
      .then(data => {
        const inv = {};
        Object.entries(data.rates||{}).forEach(([k,v]) => { if (v>0) inv[k] = 1/v; });
        setRates(inv);
      })
      .catch(()=>{});
  }, []);

  const getRate = (from, to) => {
    if (from===to) return 1;
    if (from==='ILS') return rates[to] ? 1/rates[to] : null;
    if (to==='ILS')   return rates[from] || null;
    const a = rates[from], b = rates[to];
    return a && b ? a/b : null;
  };

  // חישוב סיכום כולל
  const sharedAcc  = tripData.sharedAccommodation;
  const hotelPrice = sharedAcc?.totalPrice ? parseFloat(sharedAcc.totalPrice) : 0;

  let totalILS = hotelPrice;
  expenses.forEach(e => {
    const ils = parseFloat(e.amountILS);
    if (ils > 0) { totalILS += ils; return; }
    const amt = parseFloat(e.amount||0);
    const cur = e.currency||'LOCAL';
    if (cur==='ILS') totalILS += amt;
    else if (cur==='USD' && rates['USD']) totalILS += amt * rates['USD'];
    else if (cur==='LOCAL' && lc && rates[lc]) totalILS += amt * rates[lc];
  });

  const saveExp = async () => {
    if (!cat||!amount) return;
    setSaving(true);
    const entry = {
      id: Date.now().toString(), category:cat, currency, amount, note, date,
      amountILS: null, customCategory:'',
    };
    try {
      await updateDoc(doc(db,'trips',docId), {expenses: arrayUnion(entry)});
      setExpenses(prev=>[...prev,entry]);
      setAdding(false); setCat(''); setAmount(''); setNote('');
      setDate(new Date().toISOString().split('T')[0]);
    } catch {}
    setSaving(false);
  };

  // חישוב המרה
  const convInput = parseFloat(converterVal) || 0;
  const convResult = lc && convInput > 0 ? (() => {
    const rate = getRate(fromLocal ? lc : 'ILS', fromLocal ? 'ILS' : lc);
    return rate ? (convInput * rate).toFixed(2) : null;
  })() : null;

  return (
    <div style={{ padding:16 }}>

      {/* כרטיס סיכום */}
      <div style={{ background:`linear-gradient(135deg,${C.blue},${C.mid})`, borderRadius:18, padding:16, marginBottom:16, boxShadow:'0 4px 16px rgba(27,63,139,0.4)' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8 }}>
          <div>
            <div style={{ color:C.grey, fontSize:12 }}>סה"כ הוצאות</div>
            <div style={{ color:C.gold, fontSize:30, fontWeight:900 }}>₪ {totalILS.toFixed(0)}</div>
            {lc && (
              <div style={{ background:'rgba(255,255,255,0.08)', borderRadius:8, padding:'4px 10px', marginTop:6, fontSize:11, color:C.grey, display:'inline-block' }}>
                מטבע מקומי: {curName(lc)} {lc} {curSym(lc)}
              </div>
            )}
          </div>
          <button onClick={() => setAdding(!adding)}
            style={{ background:'rgba(212,160,23,0.2)', border:'1px solid rgba(212,160,23,0.5)', borderRadius:12, padding:'8px 14px', color:C.gold, fontWeight:700, fontSize:13, cursor:'pointer' }}>
            + חדשה
          </button>
        </div>

        {/* כפתור המרת מטבע */}
        {lc && lc !== 'ILS' && (
          <button onClick={() => setShowConverter(!showConverter)}
            style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(212,160,23,0.35)', borderRadius:10, padding:'6px 12px', color:C.gold, fontSize:12, fontWeight:700, cursor:'pointer', marginTop:4 }}>
            {curSym(lc)} = ₪ &nbsp;{rates[lc] ? rates[lc].toFixed(3) : '...'}
          </button>
        )}
      </div>

      {/* המרת מטבע */}
      {showConverter && lc && lc !== 'ILS' && (
        <div style={{ background:'rgba(27,63,139,0.2)', border:'1px solid rgba(27,63,139,0.4)', borderRadius:14, padding:14, marginBottom:14 }}>
          <div style={{ color:C.white, fontWeight:700, marginBottom:10 }}>💱 המרת מטבע</div>
          <button onClick={() => { setFromLocal(!fromLocal); setConverterVal(''); }}
            style={{ background:'rgba(27,63,139,0.3)', border:'1px solid rgba(27,63,139,0.5)', borderRadius:10, padding:'6px 12px', color:C.grey, fontSize:12, cursor:'pointer', marginBottom:10, width:'100%' }}>
            {fromLocal ? `${curSym(lc)} ${lc}` : '₪ שקל'} ⇄ {fromLocal ? '₪ שקל' : `${curSym(lc)} ${lc}`}
          </button>
          <input
            type="number" inputMode="decimal" placeholder="0"
            value={converterVal}
            onChange={e => setConverterVal(e.target.value)}
            style={{ width:'100%', background:'rgba(27,63,139,0.2)', border:'1px solid rgba(27,63,139,0.5)', borderRadius:12, padding:'12px 14px', color:C.white, fontSize:20, fontWeight:700, outline:'none', boxSizing:'border-box', direction:'ltr', marginBottom:10 }}
          />
          {convResult && (
            <div style={{ background:'rgba(212,160,23,0.1)', border:'1px solid rgba(212,160,23,0.4)', borderRadius:12, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ color:C.grey, fontSize:14 }}>{fromLocal ? '₪ שקל' : `${curSym(lc)} ${lc}`}</span>
              <span style={{ color:C.gold, fontWeight:900, fontSize:24 }}>{convResult}</span>
            </div>
          )}
          {rates[lc] && (
            <div style={{ color:C.greyDk, fontSize:11, textAlign:'center', marginTop:8 }}>
              1 {curSym(lc)} = ₪ {rates[lc].toFixed(4)}
            </div>
          )}
        </div>
      )}

      {/* טופס הוספה */}
      {adding && (
        <div style={{ background:'rgba(27,63,139,0.2)', borderRadius:14, padding:14, marginBottom:14 }}>
          {/* קטגוריות */}
          <div style={{ color:C.grey, fontSize:12, marginBottom:8 }}>קטגוריה</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:12 }}>
            {CATS.map(c => (
              <button key={c.id} onClick={() => setCat(c.id)}
                style={{ background:cat===c.id?'rgba(212,160,23,0.2)':'rgba(27,63,139,0.3)', border:`1px solid ${cat===c.id?C.gold:'rgba(27,63,139,0.5)'}`, borderRadius:10, padding:'6px 10px', cursor:'pointer', color:cat===c.id?C.gold:C.grey, fontSize:13 }}>
                {c.icon} {c.label}
              </button>
            ))}
          </div>

          {/* מטבע */}
          <div style={{ color:C.grey, fontSize:12, marginBottom:8 }}>מטבע</div>
          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            {[{code:'LOCAL',label:lc?`${curSym(lc)} מקומי`:'מקומי'},{code:'USD',label:'$ דולר'},{code:'ILS',label:'₪ שקל'}].map(o => (
              <button key={o.code} onClick={() => setCurrency(o.code)}
                style={{ flex:1, background:currency===o.code?'rgba(212,160,23,0.2)':'rgba(27,63,139,0.3)', border:`1px solid ${currency===o.code?C.gold:'rgba(27,63,139,0.5)'}`, borderRadius:10, padding:8, cursor:'pointer', color:currency===o.code?C.gold:C.grey, fontWeight:700, fontSize:12 }}>
                {o.label}
              </button>
            ))}
          </div>

          {/* תאריך */}
          <div style={{ color:C.grey, fontSize:12, marginBottom:8 }}>תאריך</div>
          <input type="date" value={date} onChange={e=>setDate(e.target.value)}
            style={{ width:'100%', background:'rgba(27,63,139,0.2)', border:'1px solid rgba(27,63,139,0.5)', borderRadius:12, padding:'10px 14px', color:C.white, fontSize:14, outline:'none', boxSizing:'border-box', marginBottom:12 }} />

          <Fld label="סכום" value={amount} onChange={setAmount} mode="decimal" />
          <Fld label="הערה (אופציונלי)" value={note} onChange={setNote} />
          <Btn label={saving?'שומר...':'הוסף הוצאה'} onClick={saveExp} disabled={saving||!cat||!amount} gold />
        </div>
      )}

      {/* מלון משותף */}
      {hotelPrice > 0 && sharedAcc?.hotelName && (
        <div style={{ background:'rgba(27,63,139,0.2)', border:'1px solid rgba(27,63,139,0.5)', borderRadius:12, padding:12, marginBottom:8, display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:22 }}>🏨</span>
          <div style={{ flex:1 }}>
            <div style={{ color:C.white, fontWeight:600, fontSize:13 }}>מלון — {sharedAcc.hotelName}</div>
            <div style={{ color:C.grey, fontSize:12 }}>עלות לינה כוללת</div>
          </div>
          <div style={{ color:C.white, fontWeight:700 }}>₪ {hotelPrice.toFixed(0)}</div>
        </div>
      )}

      {/* רשימת הוצאות */}
      {[...expenses].reverse().map((e,i) => {
        const c = ci(e.category);
        const ils = parseFloat(e.amountILS);
        let calcILS = null;
        if (!ils || ils<=0) {
          const amt = parseFloat(e.amount||0);
          const cur = e.currency||'LOCAL';
          if (cur==='USD' && rates['USD']) calcILS = (amt * rates['USD']).toFixed(0);
          else if (cur==='LOCAL' && lc && rates[lc]) calcILS = (amt * rates[lc]).toFixed(0);
        }
        return (
          <div key={i} style={{ background:'rgba(27,63,139,0.12)', border:'1px solid rgba(27,63,139,0.3)', borderRadius:12, padding:12, marginBottom:8, display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:22 }}>{c.icon}</span>
            <div style={{ flex:1 }}>
              <div style={{ color:C.white, fontWeight:600, fontSize:13 }}>{c.label}</div>
              {e.note && <div style={{ color:C.grey, fontSize:12 }}>{e.note}</div>}
              <div style={{ color:C.greyDk, fontSize:11 }}>{fmtDate(e.date)}</div>
            </div>
            <div style={{ textAlign:'left' }}>
              <div style={{ color:C.white, fontWeight:700 }}>{sym(e.currency)} {e.amount}</div>
              {(ils>0 || calcILS) && (
                <div style={{ color:C.gold, fontSize:11 }}>₪ {ils>0 ? ils.toFixed(0) : calcILS}</div>
              )}
            </div>
          </div>
        );
      })}

      {!expenses.length && !hotelPrice && <Empty icon="💸" text="אין הוצאות עדיין" />}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────

// הוסיפי את זה כאן, בסוף הקובץ
function ErrBox({ msg }) {
    return (
        <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid #EF4444',
            borderRadius: 12,
            padding: 12,
            color: '#EF4444',
            fontSize: 13,
            marginBottom: 14,
            textAlign: 'center'
        }}>
            ⚠️ {msg}
        </div>
    );
}
function Fld({ label, placeholder, value, onChange, mode, secret }) {
    return (
        <div style={{ marginBottom: 14 }}>
            {label && <div style={{ color: C.grey, fontSize: 12, marginBottom: 6 }}>{label}</div>}
            <input
                type={secret ? 'password' : mode === 'decimal' ? 'number' : 'text'}
                inputMode={mode || 'text'}
                placeholder={placeholder}
                value={value}
                onChange={e => onChange(e.target.value)}
                dir={mode === 'numeric' || mode === 'decimal' ? 'ltr' : 'rtl'}
                style={{
                    width: '100%', background: 'rgba(27,63,139,0.2)',
                    border: '1px solid rgba(27,63,139,0.5)', borderRadius: 12,
                    padding: '12px 14px', color: C.white, fontSize: 15,
                    outline: 'none', boxSizing: 'border-box',
                }}
            />
        </div>
    );
}

function Btn({ label, onClick, disabled, gold }) {
    return (
        <button onClick={onClick} disabled={disabled} style={{
            width: '100%', padding: 14, borderRadius: 14, border: 'none',
            background: disabled ? (gold ? 'rgba(212,160,23,0.4)' : C.greyDk) : (gold ? `linear-gradient(135deg,${C.gold},#b8860b)` : `linear-gradient(135deg,${C.blue},${C.mid})`),
            color: gold ? C.navy : C.white, fontWeight: 700, fontSize: 16,
            cursor: disabled ? 'default' : 'pointer',
            boxShadow: disabled ? 'none' : gold ? '0 4px 16px rgba(212,160,23,0.4)' : '0 4px 16px rgba(27,63,139,0.4)',
        }}>{label}</button>
    );
}

function Empty({ icon, text }) {
    return (
        <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
            <div style={{ color: C.grey }}>{text}</div>
        </div>
    );
}